// RagEditModal — full editor for changing a facility's RAG from the
// Facility Detail → Status tab. Heavier than RagEditPopover (the small
// card-level picker): category is REQUIRED when the status changes, and
// the reason textarea is required unless the category is "Operational"
// (the catch-all that explicitly allows status flips without narrative).
//
// On save: UPDATE facilities row with the new status + 4 attribution
// columns. audit_trigger captures the row in audit_log; the live half
// of v_facility_rag_history will pick it up on the next read.
//
// 2026-07-06 — Financial RAG dimension removed entirely (we don't track
// it). This component is operational-RAG only now.
// ============================================================================
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../shared/components';
import {
  RAG_CATEGORY_LABELS,
  RAG_COLORS,
  ragLabel,
} from '../../../shared/design-tokens';
import { saveRag } from '../lib/building-saves';
import { useAuth } from '../../../shared/auth';
import type { Facility, RagCategory, RagStatus } from '../../../shared/types';
import { overlayClickClose } from '../../../shared/overlay-close';

export interface RagEditModalProps {
  open: boolean;
  onClose: () => void;
  facility: Facility;
  onSaved: () => void;
}

const RAG_OPTIONS: RagStatus[] = ['Green', 'Amber', 'Red'];

export function RagEditModal({
  open,
  onClose,
  facility,
  onSaved,
}: RagEditModalProps) {
  const { employee } = useAuth();

  const currentStatus: RagStatus | null = facility.account_status_rag;
  const currentCategory = facility.rag_category;
  const currentReason = facility.rag_reason;

  const [status, setStatus] = useState<RagStatus | null>(currentStatus);
  const [category, setCategory] = useState<RagCategory | ''>(
    currentCategory ?? '',
  );
  const [reason, setReason] = useState(currentReason ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state whenever the modal re-opens for a different snapshot
  // of the facility (e.g. after another tab saved a change).
  useEffect(() => {
    if (open) {
      setStatus(currentStatus);
      setCategory(currentCategory ?? '');
      setReason(currentReason ?? '');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentStatus, currentCategory, currentReason]);

  if (!open) return null;

  const statusChanged = status !== currentStatus;
  const categoryRequired = statusChanged;
  // Blank reason is allowed when category='Operational' (the catch-all).
  const reasonRequired = category !== '' && category !== 'Operational';

  const validationError = (() => {
    if (!status) return 'Pick a status.';
    if (categoryRequired && !category) return 'Pick a category.';
    if (reasonRequired && reason.trim().length === 0)
      return 'Add a reason (required for this category).';
    return null;
  })();

  const canSave =
    !submitting &&
    !validationError &&
    (statusChanged || category !== '' || reason.trim().length > 0);

  async function handleSave() {
    if (!status || validationError) return;
    setSubmitting(true);
    setError(null);

    // The RAG's one save (lib/building-saves.ts), shared with the other RAG
    // editor and Ask AI's building card (item 944).
    const { error: updErr } = await saveRag({
      facilityId: facility.facility_id,
      rag: status,
      category: category || null,
      reason: reason.trim() || null,
      setByEid: employee?.employee_id ?? null,
    });

    setSubmitting(false);
    if (updErr) {
      // 42501 = RLS denial (AMs can only edit their own facilities).
      setError(updErr.message);
      return;
    }
    onSaved();
    onClose();
  }

  const heading = 'Change RAG';
  const categoryLabels = RAG_CATEGORY_LABELS;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${heading} for ${facility.facility_name}`}
      {...overlayClickClose(onClose)}
    >
      <div
        className="w-full max-w-md rounded-xl bg-tcs-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-tcs-text">{heading}</h2>
            <p className="text-xs text-tcs-text-muted">{facility.facility_name}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <fieldset className="mt-2">
          <legend className="mb-2 text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Status
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {RAG_OPTIONS.map((s) => {
              const active = status === s;
              return (
                <label
                  key={s}
                  className={`flex h-14 cursor-pointer flex-col items-center justify-center rounded-lg border-2 text-xs font-medium transition-colors ${
                    active
                      ? 'border-tcs-text bg-tcs-bg-muted'
                      : 'border-tcs-border bg-tcs-bg hover:bg-tcs-bg-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="rag-status"
                    value={s}
                    checked={active}
                    onChange={() => setStatus(s)}
                    className="sr-only"
                  />
                  <span
                    className="mb-1 inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: RAG_COLORS[s] }}
                    aria-hidden="true"
                  />
                  {ragLabel[s]}
                </label>
              );
            })}
          </div>
        </fieldset>

        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Category {categoryRequired && <span className="text-rag-red">*</span>}
          </span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as RagCategory | '')}
            className="h-10 w-full rounded-md border border-tcs-border-strong bg-tcs-bg px-3 text-sm focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
          >
            <option value="">— Select —</option>
            {(Object.keys(categoryLabels) as Array<keyof typeof categoryLabels>).map((k) => (
              <option key={k as string} value={k as string}>
                {categoryLabels[k]}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Reason {reasonRequired && <span className="text-rag-red">*</span>}
            {!reasonRequired && (
              <span className="ml-1 text-tcs-text-muted">(optional)</span>
            )}
          </span>
          <textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What changed?  This shows up in the RAG history."
            className="w-full resize-y rounded-md border border-tcs-border-strong bg-tcs-bg p-3 text-sm focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
          />
        </label>

        {(error || validationError) && (
          <div className="mt-3 rounded-md border border-rag-red/40 bg-rag-red/10 p-2 text-xs text-rag-red">
            {error || validationError}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
